import { GMXCarousel } from '../../core/carousel/carousel'
import {
	TrimModel,
	VehicleIdData,
	VehicleTrimExtraTextModel,
	VehicleTrimLinksModel,
} from '../../../models/vehicle-trim.model'
import { sanitizeInput, sanitizeUrl } from '../../../utils/sanitize'
import { calculateImwidthForImage } from '../../../utils/calculateImwidthForImage'
import { getEntryPresetParams } from '../../../utils/entry-presets'

interface CTA {
	ctaKey: string
	label: string
	icon: string
}

/**
 * Carousel - Vehicle Trim
 */
export class GMXCarouselVehicleTrim extends HTMLElement {
	trimScrollerEl: HTMLElement
	trimCarouselEl: GMXCarousel
	trimInfoContainerEl: HTMLElement
	scrollerSlides: NodeListOf<HTMLElement>
	trimNameEl: HTMLElement
	trimDescriptionEl: HTMLElement
	trimPriceEl: HTMLElement
	trimAsShownPriceEl: HTMLElement | null = null
	trimStartingAtPriceEl: HTMLElement | null = null
	ctaContainerEl: HTMLElement
	intersectionObserver: IntersectionObserver
	carouselControlsEl: HTMLElement

	private boundHandleObservationCallback = this.handleObservation.bind(this)
	private boundCarouselTransitionEndHandler = this._handleCarouselTransitionEnd.bind(this)
	private boundCarouselControlsClickHandler = this._handleCarouselControlsClick.bind(this)

	/**
	 * Holds the data for the trims
	 * @private
	 */
	private trimsJSON: TrimModel[]

	private activeTrimId: string

	get trimDataEndpointUrl(): string {
		return this.getAttribute('data-trims-data-endpoint')
	}

	get trimsFilter(): VehicleIdData[] {
		const filters = this.getAttribute('data-trims-filter')

		if (filters) {
			return JSON.parse(filters)
		} else {
			return []
		}
	}

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}

	get ctaLinks(): CTA[] {
		return this.hasAttribute('data-ctas') ? JSON.parse(this.getAttribute('data-ctas')) : []
	}

	get priceDisclosureLink(): string {
		return this.getAttribute('data-price-disclosure-link') || ''
	}
	set priceDisclosureLink(value: string) {
		this.setAttribute('data-price-disclosure-link', value)
	}

	get defaultTrimId(): string {
		return this.getAttribute('data-default-trim-id') || ''
	}

	connectedCallback(): void {
		this.trimScrollerEl = this.querySelector('.trim-scroller')
		this.trimInfoContainerEl = this.querySelector('.trim-info-container')
		this.trimNameEl = this.querySelector('.trim-name')
		this.trimDescriptionEl = this.querySelector('.trim-description')
		this.trimPriceEl = this.querySelector('.trim-price')
		this.ctaContainerEl = this.querySelector('.cta-container')
		this.carouselControlsEl = this.querySelector('.gmx-headline-controls')

		this.intersectionObserver = new IntersectionObserver(this.boundHandleObservationCallback, {
			root: null,
			rootMargin: '250px',
			threshold: 0,
		})
		this.intersectionObserver.observe(this)
	}

	disconnectedCallback(): void {
		if (this.trimCarouselEl) {
			this.trimCarouselEl.removeEventListener('transitionend', this.boundCarouselTransitionEndHandler)
		}

		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
		}

		if (this.carouselControlsEl) {
			this.carouselControlsEl.removeEventListener('click', this.boundCarouselControlsClickHandler)
		}
	}

	/**
	 * Callback for when IntersectionObserver hits the component
	 * @param entries
	 */
	handleObservation(entries: IntersectionObserverEntry[]): void {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				this.initTile()

				this.intersectionObserver.unobserve(this)
			}
		})
	}

	/**
	 * Callback to handle when the carousel controls (prev & next buttons/arrows) are clicked to move the carousel backwards or forwards.
	 * @param e
	 */
	_handleCarouselControlsClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('gmx-prev-ctrl')) {
			this.trimCarouselEl.activatePreviousSlide()
		} else if (target.classList.contains('gmx-next-ctrl')) {
			this.trimCarouselEl.activateNextSlide()
		}
	}

	/**
	 * Method called when tile comes close enough into view and is ready to load and display
	 */
	async initTile(): Promise<void> {
		this.trimsJSON = await this.getTrimData(this.trimDataEndpointUrl)

		if (this.trimsJSON.length) {
			this.buildCarousel()

			// Prefer `?trim=`, then `data-default-trim-id`, then the VDM default trim.
			this._setInitialSlide()

			// Initialize by calling the handle scroll event
			this._handleCarouselTransitionEnd()

			if (this.carouselControlsEl) {
				this.carouselControlsEl.addEventListener('click', this.boundCarouselControlsClickHandler)
			}
		}
	}

	/**
	 * Sets the initial carousel slide, in order of precedence:
	 * 1. A matching `?trim=` entry parameter
	 * 2. The authored `data-default-trim-id` attribute
	 * 3. The VDM default trim (`trimDefaults.isDefaultTrim`)
	 * 4. The first slide
	 */
	private _setInitialSlide(): void {
		if (!this.trimCarouselEl) {
			return
		}

		const findSlideIndexByTrimId = (trimId: string): number =>
			Array.from(this.scrollerSlides).findIndex(slide => slide.getAttribute('data-trim-id') === trimId)

		const presetTrim = getEntryPresetParams().trim
		if (presetTrim) {
			const presetIndex = findSlideIndexByTrimId(presetTrim)
			if (presetIndex >= 0) {
				this.trimCarouselEl.goToSlide(presetIndex)
				return
			}
		}

		if (this.defaultTrimId) {
			const authoredDefaultIndex = findSlideIndexByTrimId(this.defaultTrimId)
			if (authoredDefaultIndex >= 0) {
				this.trimCarouselEl.goToSlide(authoredDefaultIndex)
				return
			}
		}

		const vdmDefaultTrimIndex = this.trimsJSON.findIndex(trim => trim.trimDefaults?.isDefaultTrim === true)
		this.trimCarouselEl.goToSlide(vdmDefaultTrimIndex >= 0 ? vdmDefaultTrimIndex : 0)
	}

	/**
	 * Given a URL of a location of the colorizer instance data, fetch and return the result of the GET
	 * @param url {String} - URL of the location of the colorizer instance data
	 */
	async getTrimData(url: string): Promise<TrimModel[]> {
		let retTrimData: TrimModel[] = []

		try {
			const response = await fetch(url)

			if (response.ok) {
				const data = await response.json()
				const allTrims = data?.data?.trims

				if (allTrims) {
					if (this.trimsFilter.length) {
						this.trimsFilter.forEach(tf => {
							const filteredTrim = allTrims.find((t: TrimModel) => t.code === tf.vehicleId)
							if (filteredTrim) {
								if (tf.tagline !== undefined && tf.tagline !== '') {
									const trimCarouselTagline = filteredTrim.extraText.find(
										(et: VehicleTrimExtraTextModel) => et.extraTextKey === 'trimCarouselTagline'
									)

									if (trimCarouselTagline) {
										trimCarouselTagline.extraTextValue = tf.tagline
									} else {
										const newExtraText: VehicleTrimExtraTextModel = {
											extraTextKey: 'trimCarouselTagline',
											extraTextValue: tf.tagline,
										}
										filteredTrim.extraText.push(newExtraText)
									}
								}
								retTrimData.push(filteredTrim)
							}
						})
					} else {
						retTrimData = allTrims
					}
				}
			} else {
				throw new Error(`return status: ${response.status}`)
			}
		} catch (error) {
			window.console.error(`ERROR:  Carousel Vehicle Trim: Failed to retrieve trim data ${error}`)
		}

		return retTrimData
	}

	/**
	 * Build Carousel
	 */
	buildCarousel(): void {
		this.trimCarouselEl = document.createElement('gmx-carousel') as GMXCarousel
		this.trimCarouselEl.setAttribute('data-offset-w-peek', 'true')

		const carouselCollectionEl = document.createElement('div')
		carouselCollectionEl.classList.add('slides-collection')

		this.trimsJSON.forEach(trim => {
			const trimImg = trim.images.find(img => img.imageID === 'default_image') || trim.images[0]

			const slideEl = document.createElement('div')
			slideEl.classList.add('slide')
			slideEl.setAttribute('data-trim-id', sanitizeInput(trim.code))
			slideEl.setAttribute('data-as-shown-price', trimImg.imageAsShownPrice || '')

			const slideWidth = this.offsetWidth * 0.8 // Using 80% of the tile width for the image to allow room for peek and prevent loading unnecessarily large images
			const slideImgUrl = trimImg?.imageURL
				? trimImg.imageURL.match(/\?.*$/)
					? `${sanitizeUrl(trimImg.imageURL)}&imwidth=${calculateImwidthForImage(slideWidth)}`
					: `${sanitizeUrl(trimImg.imageURL)}?imwidth=${calculateImwidthForImage(slideWidth)}`
				: ''

			const slideImg = document.createElement('img') as HTMLImageElement
			slideImg.src = slideImgUrl
			slideImg.alt = sanitizeInput(trim.title)

			slideEl.appendChild(slideImg)

			carouselCollectionEl.appendChild(slideEl)
		})

		this.trimCarouselEl.innerHTML = ''
		this.trimCarouselEl.appendChild(carouselCollectionEl)

		this.trimScrollerEl.appendChild(this.trimCarouselEl)
		this.scrollerSlides = this.trimScrollerEl.querySelectorAll('.slide')

		this.trimCarouselEl.addEventListener('transitionend', this.boundCarouselTransitionEndHandler)
	}

	/**
	 * Callback to handle scroller slide events.  Will loop through slides and find the one fully in the tile
	 * container.
	 * @private
	 */
	private _handleCarouselTransitionEnd(): void {
		const tileClientRect = this.getBoundingClientRect()

		/* Loop through slides and find the one that fits within the tile */
		this.scrollerSlides.forEach(slide => {
			const slideClientRect = slide.getBoundingClientRect()

			if (slideClientRect.left >= tileClientRect.left && slideClientRect.right <= tileClientRect.right) {
				const activeTrim = slide.getAttribute('data-trim-id')

				if (activeTrim !== this.activeTrimId) {
					const trim = this.trimsJSON.filter(t => t.code === slide.getAttribute('data-trim-id'))[0]
					const currentTrimInfo = this.trimInfoContainerEl.querySelector('.trim-info') as HTMLElement
					const oldTrimInfoContainer = currentTrimInfo.cloneNode(true) as HTMLElement
					oldTrimInfoContainer.classList.add('prev-trim')
					currentTrimInfo.after(oldTrimInfoContainer)

					this.trimNameEl.innerText = trim.title
					const trimTagline =
						trim.extraText?.find(et => et.extraTextKey === 'trimCarouselTagline')?.extraTextValue || ''

					this.trimDescriptionEl.innerHTML =
						window.gmxDOMPurify?.sanitize?.(trimTagline) || sanitizeInput(trimTagline)
					this.ctaContainerEl.innerHTML = this._buildSlideCTAList(trim.links)

					this.trimAsShownPriceEl = this.trimPriceEl.querySelector('.trim-as-shown')

					// Build starting at price with disclosure
					if (trim.price) {
						const startingAtPriceDisclosureEl = document.createElement('span')
						startingAtPriceDisclosureEl.setAttribute('data-legal-uri', this.priceDisclosureLink)
						startingAtPriceDisclosureEl.textContent = trim.price

						const existingStartingAtEl: HTMLElement = this.trimPriceEl.querySelector('.trim-starting-at')
						if (existingStartingAtEl) {
							this.trimStartingAtPriceEl = existingStartingAtEl
						} else {
							this.trimStartingAtPriceEl = document.createElement('span')
							this.trimStartingAtPriceEl.classList.add('trim-starting-at')
							if (this.trimAsShownPriceEl) {
								this.trimPriceEl.insertBefore(this.trimStartingAtPriceEl, this.trimAsShownPriceEl)
							} else {
								this.trimPriceEl.appendChild(this.trimStartingAtPriceEl)
							}
						}

						this.trimStartingAtPriceEl.innerText = `${this.labelData?.i18n_startingAt || ''} `
						this.trimStartingAtPriceEl.appendChild(startingAtPriceDisclosureEl)
					} else {
						if (this.trimStartingAtPriceEl) {
							this.trimStartingAtPriceEl.remove()
							this.trimStartingAtPriceEl = null
						}
					}

					// Build as shown price with disclosure
					const asShownPrice = slide.getAttribute('data-as-shown-price') || ''

					if (asShownPrice) {
						const asShownPriceDisclosureEl = document.createElement('span')
						asShownPriceDisclosureEl.setAttribute('data-legal-uri', this.priceDisclosureLink)
						asShownPriceDisclosureEl.textContent = asShownPrice

						if (!this.trimAsShownPriceEl) {
							this.trimAsShownPriceEl = document.createElement('span')
							this.trimAsShownPriceEl.classList.add('trim-as-shown')
							this.trimPriceEl.appendChild(this.trimAsShownPriceEl)
						}

						this.trimAsShownPriceEl.innerText = `${this.labelData?.i18n_asShown || ''} `
						this.trimAsShownPriceEl.appendChild(asShownPriceDisclosureEl)
					} else {
						if (this.trimAsShownPriceEl) {
							this.trimAsShownPriceEl.remove()
							this.trimAsShownPriceEl = null
						}
					}

					currentTrimInfo.addEventListener(
						'animationend',
						() => {
							currentTrimInfo.classList.remove('animate')
						},
						{ once: true }
					)

					oldTrimInfoContainer.addEventListener(
						'animationend',
						() => {
							this.trimInfoContainerEl.removeChild(oldTrimInfoContainer)
						},
						{ once: true }
					)
					oldTrimInfoContainer.classList.add('animate')
					currentTrimInfo.classList.add('animate')

					this.activeTrimId = slide.getAttribute('data-trim-id')
				}
			}
		})
	}

	/**
	 * Build CTA Markup for Model Slide
	 * @private
	 */
	private _buildSlideCTAList(trimCTAs: VehicleTrimLinksModel[]): string {
		const ctaContainerEl = document.createElement('div')

		// Get the number of matched CTAs so we know if to use a secondary or tertiary button for CTAs beyond the first
		const numMatchedCTAs = trimCTAs.filter(cta => this.ctaLinks.map(cl => cl.ctaKey).includes(cta.linkID)).length

		let numCTAsAdded = 0
		this.ctaLinks.forEach((link: CTA) => {
			const trimLink = trimCTAs.filter(cta => cta.linkID === link.ctaKey)[0]

			if (trimLink) {
				const ctaEl: HTMLButtonElement | HTMLAnchorElement =
					trimLink.primaryLink_type === 'fragment'
						? document.createElement('button')
						: document.createElement('a')
				ctaEl.classList.add(
					'gmx-btn',
					numCTAsAdded === 0
						? 'gmx-btn-primary'
						: numMatchedCTAs === 2
							? 'gmx-btn-secondary'
							: 'gmx-btn-tertiary'
				)

				ctaEl.textContent = trimLink.primaryLink_labelOverride || link.label || 'Label Missing'

				if (link.icon) {
					ctaEl.classList.add('icon-left')
					ctaEl.dataset.iconName = link.icon
					ctaEl.dataset.iconPosition = 'before'
				}

				switch (trimLink.primaryLink_type) {
					case 'internal':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${trimLink.primaryLink_internalUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							trimLink.primaryLink_internalTarget || '_self'
						)
						break
					case 'external':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${trimLink.primaryLink_externalUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							trimLink.primaryLink_externalTarget || '_blank'
						)
						break
					case 'relative':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${trimLink.primaryLink_relativeUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							trimLink.primaryLink_relativeTarget || '_self'
						)
						break
					case 'anchor':
						;(ctaEl as HTMLAnchorElement).href = `#${sanitizeInput(trimLink.primaryLink_anchor)}`
						break
					case 'fragment':
						;(ctaEl as HTMLButtonElement).type = 'button'

						if (trimLink.primaryLink_xfType === 'expanded-tile') {
							// Is an overlay
							ctaEl.dataset.overlayRevealId = trimLink.primaryLink_xfPath
							ctaEl.dataset.overlayType = trimLink.primaryLink_xfType
						} else {
							// Is a panel
							ctaEl.dataset.panelRevealId = trimLink.primaryLink_xfPath
							ctaEl.dataset.panelType = trimLink.primaryLink_xfType

							if (ctaEl.dataset.panelType === 'form-panel' && trimLink.vehicleSelectorModelYear) {
								ctaEl.dataset.panelData = JSON.stringify({
									vehicleSelectorYear: trimLink.vehicleSelectorModelYear,
									vehicleSelectorModel: trimLink.vehicleSelectorModelName || '',
								})
							}
						}
						break
					default:
						break
				}

				ctaContainerEl.appendChild(ctaEl)
				numCTAsAdded++
			}
		})

		return ctaContainerEl.innerHTML
	}
}

if (!customElements.get('gmx-carousel-vehicle-trim')) {
	customElements.define('gmx-carousel-vehicle-trim', GMXCarouselVehicleTrim)
}
